import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import {
  AiDocumentReviewAllowedActions,
  AiDocumentReviewMode,
} from 'src/constants/ai-document-review/enums'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import type { AiDocumentReviewConfigFormValues } from 'src/interfaces/aiDocumentReviewConfig'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import { AiReviewConfigsTableFields } from './enums'

/** Mirrors `DOCUMENT_AI_EXTRACTION_SLP_RATE_MIN`/`MAX` in para-node. */
export const AI_REVIEW_SLP_RATE_MIN = 0
export const AI_REVIEW_SLP_RATE_MAX = 100

/** Mirrors `AI_DOCUMENT_REVIEW_DEFAULT_AUTO_APPROVE_MIN_SLP_RATE` in para-node. */
export const AI_REVIEW_DEFAULT_AUTO_APPROVE_MIN_SLP_RATE = 80

export const AI_REVIEW_MODE_OPTIONS: BasicSelectOption[] = [
  { label: 'Shadow — record the decision only', value: AiDocumentReviewMode.Shadow },
  { label: 'Active — act on the step', value: AiDocumentReviewMode.Active },
]

export const AI_REVIEW_ALLOWED_ACTIONS_OPTIONS: BasicSelectOption[] = [
  { label: 'Approve and decline', value: AiDocumentReviewAllowedActions.All },
  { label: 'Approve only', value: AiDocumentReviewAllowedActions.ApproveOnly },
  { label: 'Decline only', value: AiDocumentReviewAllowedActions.DeclineOnly },
  {
    label: 'Neither — always leave the step for a person',
    value: AiDocumentReviewAllowedActions.None,
  },
]

/** Card-sized wording for the same values. */
export const AI_REVIEW_ALLOWED_ACTIONS_LABELS: Record<AiDocumentReviewAllowedActions, string> = {
  [AiDocumentReviewAllowedActions.All]: 'Approve · Decline',
  [AiDocumentReviewAllowedActions.ApproveOnly]: 'Approve only',
  [AiDocumentReviewAllowedActions.DeclineOnly]: 'Decline only',
  [AiDocumentReviewAllowedActions.None]: 'None',
}

/** Every step type the backend accepts, ordered the way the flow editor groups them. */
export const AI_REVIEW_STEP_TYPE_OPTIONS: BasicSelectOption[] = Object.values(CollectDataStepType)
  .map((stepType) => {
    return { label: stepLabel(stepType), value: stepType }
  })
  .sort((first, second) => {
    return first.label.localeCompare(second.label)
  })

export const AI_REVIEW_CONFIG_INITIAL_VALUES: AiDocumentReviewConfigFormValues = {
  stepType: '',
  label: '',
  enabled: true,
  mode: AiDocumentReviewMode.Shadow,
  allowedActions: AiDocumentReviewAllowedActions.All,
  instructions: '',
  useInternalReviewInstructions: true,
  hasAutoApproveMinSlpRate: false,
  autoApproveMinSlpRate: AI_REVIEW_DEFAULT_AUTO_APPROVE_MIN_SLP_RATE,
  identityMatchRequired: true,
  vehicleMatchRequired: true,
  expirationCheckRequired: true,
}

export const AI_REVIEW_ANY_LABEL_TEXT = 'Every step of this type'

export const aiReviewConfigsTableHeaderTitles: Record<AiReviewConfigsTableFields, string> = {
  [AiReviewConfigsTableFields.stepType]: 'Step type',
  [AiReviewConfigsTableFields.mode]: 'Mode',
  [AiReviewConfigsTableFields.label]: 'Step label',
  [AiReviewConfigsTableFields.allowedActions]: 'Allowed actions',
  [AiReviewConfigsTableFields.autoApproveMinSlpRate]: 'Auto-approve from',
  [AiReviewConfigsTableFields.checks]: 'Checks',
  [AiReviewConfigsTableFields.instructions]: 'Instructions',
  // The actions column is a menu trigger only; a header would just add noise.
  [AiReviewConfigsTableFields.actions]: '',
}

// A partner has a handful of configurations — one per document at most.
export const aiReviewConfigPageSizeOptions = [25, 50, 100]

export const AI_REVIEW_CONFIGS_GRID_COLUMNS_STORAGE_KEY = 'admin.aiReviewConfigs.gridColumns'
